import React, { useState, useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "../css/maps.scss";
import _t from "../utils/translate";
import Spinner from "./Spinner";
import { useMarkerData } from "../context/MarkerDataContext";
import iconTarget from "../img/icons/icon_target.svg";

// Configurer les icônes de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const Maps = () => {
  const { locations, loading } = useMarkerData();
  const savedFilter =
    localStorage.getItem("selectedResourcesFilter") || "Pharmacy";
  const [filter, setFilter] = useState(savedFilter);
  const mapRef = useRef(null);
  const markersRef = useRef(null);
  const mapContainerRef = useRef(null);

  const filterOptions = [
    "Pharmacy",
    "Drugstore",
    "Doctor",
    "Homeopath",
    "Naturopath",
    "Dentist",
    "Midwife",
    "Veterinarian",
    "Other therapist",
  ];

  const initialMapState = {
    center: [46.8182, 8.2275],
    zoom: 8,
  };

  // Sauvegarde du filtre dans le localStorage
  useEffect(() => {
    localStorage.setItem("selectedResourcesFilter", filter);
  }, [filter]);

  // Initialisation de la carte - une seule fois
  useEffect(() => {
    if (loading || !mapContainerRef.current || mapRef.current) return;

    // Créer une nouvelle carte
    const leafletMap = L.map(mapContainerRef.current, {
      center: initialMapState.center,
      zoom: 8,
      minZoom: 4,
    });

    // Ajouter la couche de tuiles
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(leafletMap);

    // Ajouter le bouton pour recentrer la carte
    const recenterButton = L.control({ position: "bottomright" });
    recenterButton.onAdd = () => {
      const div = L.DomUtil.create("div", "recenter-button");
      div.innerHTML = `<img src="${iconTarget}" alt="Recenter" />`;
      div.onclick = () => {
        leafletMap.setView(initialMapState.center, initialMapState.zoom);
      };
      return div;
    };
    recenterButton.addTo(leafletMap);

    // Sauvegarder la référence à la carte
    mapRef.current = leafletMap;

    // Nettoyage lors du démontage du composant
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [loading]);

  // Mise à jour des marqueurs lorsque filter ou locations change
  useEffect(() => {
    if (loading || !mapRef.current || !locations) return;

    // Si des marqueurs existent déjà, les supprimer
    if (markersRef.current) {
      mapRef.current.removeLayer(markersRef.current);
    }

    // Créer un nouveau groupe de marqueurs clustérisés
    const markerGroup = L.markerClusterGroup({
      iconCreateFunction: (cluster) => {
        return L.divIcon({
          html: `<div style="background-color: #2A93EE; color: white; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;">
                  ${cluster.getChildCount()}
                </div>`,
          className: "custom-cluster-icon",
          iconSize: L.point(30, 30, true),
        });
      },
    });

    // Filtrer et ajouter les marqueurs
    const filteredLocations = locations.filter((location) =>
      new RegExp(_t(filter), "i").test(location.category)
    );

    filteredLocations.forEach((location) => {
      const marker = L.marker([location.latitude, location.longitude]);
      const popupContent = `
        <strong>${location.name}</strong><br />
        ${location.address}<br />
        ${
          filter === "Other therapist" && location.function
            ? `<span><em>${location.function}</em></span><br />`
            : ""
        }
        ${
          location.website
            ? `<a href="${location.website}" target="_blank" rel="noopener noreferrer"><em>${location.website}</em></a>`
            : ""
        }
      `;
      marker.bindPopup(popupContent);
      markerGroup.addLayer(marker);
    });

    // Ajouter le groupe de marqueurs à la carte
    mapRef.current.addLayer(markerGroup);
    markersRef.current = markerGroup;
  }, [loading, locations, filter]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-title">{_t("Loading maps")}</div>
        <Spinner />
      </div>
    );
  }

  return (
    <div className="maps-container">
      <div className="map-toggle">
        {filterOptions.map((option) => (
          <button
            key={option}
            className={`toggle-button ${filter === option ? "active" : ""}`}
            onClick={() => handleFilterChange(option)}
          >
            {_t(option)}
          </button>
        ))}
      </div>
      <div className="map-wrapper">
        <div
          ref={mapContainerRef}
          id="map-container"
          style={{ height: "100%", width: "100%" }}
        />
      </div>
    </div>
  );
};

export default Maps;
